<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/authStore';
import { useTenantStore } from '@/stores/tenantStore';
import { useSearchStore } from '@/stores/searchStore';
import { useKeyboardShortcutsStore } from '@/stores/keyboardShortcutsStore';
import { useFeatures } from '@/composables/useFeatures';
import { useKeyboardShortcut } from '@/composables/useKeyboardShortcut';
import GlobalSearchResults from './GlobalSearchResults.vue';
import { Search, Bell, LogOut, Settings, Menu, Keyboard } from 'lucide-vue-next';

const emit = defineEmits<{
  toggleSidenav: [];
}>();

const router = useRouter();
const authStore = useAuthStore();
const tenantStore = useTenantStore();
const searchStore = useSearchStore();
const keyboardShortcutsStore = useKeyboardShortcutsStore();
const { flags } = useFeatures();
const showDropdown = ref(false);
const searchInputRef = ref<HTMLInputElement | null>(null);
const searchResultsRef = ref<InstanceType<typeof GlobalSearchResults> | null>(null);
const searchFocused = ref(false);

function onSearchInput(e: Event) {
  searchStore.runSearch((e.target as HTMLInputElement).value);
}

function onSearchKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') {
    searchStore.clear();
    (e.target as HTMLInputElement).blur();
    return;
  }
  searchResultsRef.value?.onKeydown(e);
}

function onSearchBlur() {
  // Defer the unmount so a click on a result row still fires before the
  // dropdown is torn down.
  setTimeout(() => { searchFocused.value = false; }, 150);
}

// "/" focuses the global search from anywhere in the app. Gated on the
// feature flag so it's a no-op when global search is disabled.
// Skip when Shift is held — Shift+/ is `?` which AppShell uses to open
// the keyboard-shortcut cheat sheet (NS-4). Without this guard both
// bindings fire on the same physical keystroke.
useKeyboardShortcut({
  key: '/',
  handler: (e) => {
    if (e.shiftKey) return;
    if (!flags.value.globalSearch) return;
    e.preventDefault();
    searchInputRef.value?.focus();
  },
});

const userInitials = computed(() => {
  const name = authStore.user?.name || 'U';
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
});

function handleLogout() {
  authStore.logout();
  router.push('/login');
}

function handleAvatarBlur() {
  setTimeout(() => { showDropdown.value = false; }, 150);
}
</script>

<template>
  <header class="top-bar flex items-center justify-between gap-4 px-6 py-3 border-b border-border-default bg-surface-900">
    <!-- Left: Menu toggle + Branding -->
    <div class="top-bar-left flex items-center gap-4">
      <button
        class="lg:hidden p-2 hover:bg-surface-700 rounded-md cwr-motion"
        aria-label="Toggle sidebar"
        @click="emit('toggleSidenav')"
      >
        <Menu class="w-5 h-5 text-text-secondary" />
      </button>

      <div class="top-bar-branding flex flex-col gap-1 items-start">
        <img
          v-if="tenantStore.branding?.logoUrl"
          :src="tenantStore.branding.logoUrl"
          :alt="tenantStore.branding.name"
          class="top-bar-branding-logo h-8 w-auto max-w-none object-contain"
        />
        <span v-else class="text-sm font-semibold text-text-primary">
          {{ tenantStore.branding?.name || 'CWR' }}
        </span>
        <span class="top-bar-branding-tagline text-[10px] leading-none text-text-muted">
          Powered by Cyber War Research
        </span>
      </div>
    </div>

    <!-- Center: Global Search (E17) -->
    <div class="top-bar-search hidden md:flex flex-1 max-w-sm">
      <div class="relative w-full" data-qa-search-root>
        <Search class="absolute left-3 top-2.5 w-4 h-4 text-text-muted pointer-events-none" />
        <input
          ref="searchInputRef"
          type="text"
          :value="searchStore.query"
          :placeholder="flags.globalSearch ? 'Search threats, IOCs, rules… ( / )' : 'Search threats, IOCs, CVEs...'"
          :disabled="!flags.globalSearch"
          class="top-bar-search-input w-full bg-surface-800 border border-border-default rounded-lg pl-10 pr-4 py-2 text-sm text-text-secondary placeholder:text-text-disabled focus:outline-none focus:border-accent-blue focus:ring-1 focus:ring-accent-blue disabled:opacity-60 disabled:cursor-not-allowed cwr-motion"
          data-qa-search-input
          @input="onSearchInput"
          @keydown="onSearchKeydown"
          @focus="searchFocused = true"
          @blur="onSearchBlur"
        />
        <GlobalSearchResults
          v-if="flags.globalSearch && searchFocused"
          ref="searchResultsRef"
        />
      </div>
    </div>

    <!-- Right: Shortcuts hint + Notifications + Profile -->
    <div class="top-bar-right flex items-center gap-3">
      <button
        class="top-bar-shortcuts-btn p-2 hover:bg-surface-700 rounded-md cwr-motion text-text-secondary"
        title="Keyboard shortcuts (?)"
        aria-label="Keyboard shortcuts"
        data-qa-topbar-shortcuts
        @click="keyboardShortcutsStore.openModal()"
      >
        <Keyboard class="w-5 h-5" />
      </button>

      <button
        class="p-2 hover:bg-surface-700 rounded-md cwr-motion relative"
        aria-label="Notifications"
      >
        <Bell class="w-5 h-5 text-text-secondary" />
        <span class="absolute top-1.5 right-1.5 w-2 h-2 bg-severity-critical rounded-full"></span>
      </button>

      <!-- User Profile -->
      <div class="relative">
        <button
          class="top-bar-avatar flex items-center justify-center w-8 h-8 rounded-full bg-accent-blue text-white text-xs font-semibold hover:opacity-80 cwr-motion"
          aria-label="User profile"
          @click="showDropdown = !showDropdown"
          @blur="handleAvatarBlur"
        >
          {{ userInitials }}
        </button>

        <!-- Dropdown -->
        <div
          v-if="showDropdown"
          class="top-bar-dropdown absolute right-0 mt-2 w-48 bg-surface-800 border border-border-default rounded-lg shadow-lg z-50"
        >
          <div class="px-4 py-3 border-b border-border-default">
            <div class="text-sm font-medium text-text-primary truncate">{{ authStore.user?.name }}</div>
            <div class="text-xs text-text-muted truncate">{{ authStore.user?.email }}</div>
          </div>
          <button
            class="w-full text-left px-4 py-2 text-sm text-text-secondary hover:bg-surface-700 flex items-center gap-2 cwr-motion"
            @mousedown.prevent="router.push('/settings')"
          >
            <Settings class="w-4 h-4" />
            Settings
          </button>
          <button
            class="w-full text-left px-4 py-2 text-sm text-severity-critical hover:bg-surface-700 flex items-center gap-2 cwr-motion border-t border-border-default"
            @mousedown.prevent="handleLogout"
          >
            <LogOut class="w-4 h-4" />
            Sign out
          </button>
        </div>
      </div>
    </div>
  </header>
</template>
